import {
    IonAlert,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonTitle,
    IonToolbar,
    RefresherEventDetail,
    useIonRouter
} from "@ionic/react";
import {
    calculateFantasyGame,
    FantasyRosterPlayer,
    FantasySettings,
    RealMatch
} from "../services/fantasyGameCalculator";

import {
    arrowBackOutline,
    americanFootballOutline,
    medkitOutline,
    personOutline,
    refreshOutline,
    settingsOutline,
    shirtOutline,
    trophyOutline
} from "ionicons/icons";

import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    useLocation,
    useParams
} from "react-router-dom";

import {
    FantasyMainTab,
    getFantasyMainTab,
    getFantasyMainTabPath
} from "../utils/fantasyNavigation";

import {
    getFantasyInjuryInfo
} from "../utils/fantasyInjury";

import {
    DEFAULT_SCHEDULE_TIMEZONE,
    formatLocalMatchSchedule
} from "../services/fantasySchedule";

import FantasyHeaderActions from "../components/FantasyHeaderActions";

import {
    isAuthenticated
} from "../services/auth";

import {
    FantasyApiError,
    fantasyApiGet,
    fantasyApiPost
} from "../services/fantasyApi";

import "./FantasyTeam.css";
import "./FantasyNavigation.css";

import FantasyPlayerModal from "../components/FantasyPlayerModal";

interface FantasyApiRosterPlayer
    extends FantasyRosterPlayer {
    isLocked?: boolean;
    injury?: string;
}

interface TeamPlayer {
    id: string;
    name: string;
    positionId: string;
    teamId: string;
    teamName: string;
    fantasySlot: string;
    points: number;
    statLine: string;
    matchup: string;
    initialProjection: number | null;
    isLocked: boolean;
    injury?: string;
}

interface RosterSlot {
    id: string;
    position: string;
    player?: TeamPlayer;
    bench: boolean;
}

interface TeamProjectionData {
    totalProjectedPoints: number;
    starterPlayerIds: string[];
    players: Record<string, number>;
}

interface LiveProjectionPlayer {
    playerId: string;
    projection: number;
    pregameProjection: number;
    actualPoints: number;
    expectedPointsToDate: number;
    performanceDelta: number;
    remainingProjection: number;
    gameProgress: number;
    sensitivity: number;
    gameQuarter: number | null;
    gameStatus: string;
    isStarter: boolean;
    realTeamId: string;
}

interface TeamProjectionForecastData {
    totalProjectedPoints: number;
    starterProjectedPoints?: number;
    benchProjectedPoints?: number;
    starterPlayerIds: string[];

    players: Record<
        string,
        LiveProjectionPlayer | number
    >;
}

interface ProjectionForecast {
    updatedAt: string;
    realRound: number;
    fantasyRound: number;
    sourceIndexGeneratedAt: string;
    method?: string;

    myTeam: TeamProjectionForecastData | null;
    opponentTeam: TeamProjectionForecastData | null;
}

interface ProjectionSnapshot {
    capturedAt: string;
    realRound: number;
    fantasyRound: number;
    sourceIndexGeneratedAt: string;

    myTeam: TeamProjectionData | null;
    opponentTeam: TeamProjectionData | null;
}

interface FantasyTradePlayer {
    id: string;
    name: string;
    position: string;
    teamName: string;
    isLocked: boolean;
}

interface FantasyIncomingTrade {
    id: string;

    createdAt: string;

    /*
        El equipo contrario a quien consulta la pantalla.
        En recibidas: quien envió la oferta.
        En enviadas: quien recibió la oferta.
    */
    counterpartyTeamId: string;
    counterpartyTeamName: string;
    counterpartyManagerName: string;

/*
    Los jugadores conservan la perspectiva original
    de la propuesta:

    - offeredPlayer: jugador ofrecido por quien creó el trade.
    - requestedPlayer: jugador solicitado por quien creó el trade.

    En una propuesta recibida:
    - offeredPlayer se recibe.
    - requestedPlayer se entrega.

    En una propuesta enviada:
    - offeredPlayer se ofrece.
    - requestedPlayer se solicita.
*/

    offeredPlayer: FantasyTradePlayer;
    requestedPlayer: FantasyTradePlayer;
}

interface FantasyLivePlayerScore {
    id: string;
    points: number;
}

interface FantasyLiveTeamScore {
    points: number;
    starterPoints: number;
    benchPoints: number;
    players: FantasyLivePlayerScore[];
}

interface FantasyLiveRound {
    realRound: number;
    fantasyRound: number;
    updatedAt: string;
    teams: Record<string, FantasyLiveTeamScore>;
}

interface FantasyTeamState {
    success: boolean;

    scheduleTimezone?: string;

    league: {
        id: string;
        name: string;
    };

    week: {
        realRound: number;
        fantasyRound: number;
        status: string;
    };

    projectionSnapshot?: ProjectionSnapshot | null;
    projectionForecast?: ProjectionForecast | null;

    fantasyMatch: {
        id: string;
        isBye: boolean;

        myTeam: {
            id: string;
            name: string;
            manager: string;
            roster: FantasyApiRosterPlayer[];
        };

        opponentTeam: {
            id: string;
            name: string;
            manager: string;
            roster: FantasyApiRosterPlayer[];
        };

    };

    settings: FantasySettings;

    rosterSettings?: Record<string, string | number>;

    realMatches: RealMatch[];
    trades?: {
        received: FantasyIncomingTrade[];
        sent: FantasyIncomingTrade[];
    };

liveRound?: FantasyLiveRound | null;

}

const rosterPositions = [
    "QB",
    "RB",
    "WR",
    "FLEX",
    "K",
    "DEF",
    "BN"
] as const;

function formatPoints(points: number): string {
    return points.toFixed(2);
}

function getPlayerPositionLabel(
    positionId: string
): string {
    const positions: Record<string, string> = {
        "1": "QB",
        "2": "RB",
        "3": "WR",
        "7": "K",
        "8": "DEF"
    };

    return positions[positionId] || "—";
}

function getOfficialTeamPlayerPoints(
    liveRound: FantasyLiveRound | null | undefined,
    fantasyTeamId: string
): Record<string, number> {
    const teamScore = liveRound?.teams?.[fantasyTeamId];

    if (!teamScore || !Array.isArray(teamScore.players)) {
        return {};
    }

    const pointsByPlayerId: Record<string, number> = {};

    teamScore.players.forEach(function (player) {
        const playerId = String(player.id || "").trim();
        const points = Number(player.points);

        if (
            playerId !== "" &&
            Number.isFinite(points)
        ) {
            pointsByPlayerId[playerId] = points;
        }
    });

    return pointsByPlayerId;
}

function getPlayerRosterPosition(player: {
    positionId: string;
    fantasySlot: string;
}): string {
    const slot = String(
        player.fantasySlot || ""
    )
        .trim()
        .toUpperCase();

    /*
        Formato moderno:
        slot = QB, RB, WR, FLEX, K, DEF o BN.
    */
    if (
        [
            "QB",
            "RB",
            "WR",
            "FLEX",
            "K",
            "DEF",
            "BN"
        ].includes(slot)
    ) {
        return slot;
    }

    /*
        Compatibilidad con el formato histórico.
    */
    if (slot === "1") {
        return "FLEX";
    }

    if (slot === "3") {
        return "BN";
    }

    if (player.positionId === "1") {
        return "QB";
    }

    if (player.positionId === "2") {
        return "RB";
    }

    if (player.positionId === "3") {
        return "WR";
    }

    if (player.positionId === "7") {
        return "K";
    }

    if (player.positionId === "8") {
        return "DEF";
    }

    return "BN";
}

function getRosterSetting(
    settings: Record<string, string | number>,
    position: string
): number {
    const keys: Record<string, string[]> = {
        QB: ["qb", "QB"],
        RB: ["rb", "RB"],
        WR: ["wr", "WR"],
        FLEX: ["wb", "WB", "flex", "FLEX"],
        K: ["k", "K"],
        DEF: ["def", "DEF"],
        BN: ["bn", "BN"]
    };

    for (const key of keys[position] || []) {
        const value = Number(settings[key]);

        if (Number.isFinite(value) && value > 0) {
            return value;
        }
    }

    return 0;
}

function createRosterSlots(
    rosterSettings: Record<string, string | number>,
    players: TeamPlayer[]
): RosterSlot[] {
    const slots: RosterSlot[] = [];

    const playersByPosition: Record<
        string,
        TeamPlayer[]
    > = {
        QB: [],
        RB: [],
        WR: [],
        FLEX: [],
        K: [],
        DEF: [],
        BN: []
    };

    players.forEach(function (player) {
        const position = getPlayerRosterPosition(player);

        playersByPosition[position].push(player);
    });

    rosterPositions.forEach(function (position) {
        const slotAmount = getRosterSetting(
            rosterSettings,
            position
        );

        const positionPlayers =
            playersByPosition[position] || [];

        for (
            let index = 0;
            index < slotAmount;
            index += 1
        ) {
            slots.push({
                id: `${position}-${index}`,
                position,
                player: positionPlayers[index],
                bench: position === "BN"
            });
        }
    });

    /*
        Muestra también jugadores en banca aunque el número de
        jugadores supere la configuración original de banca.
    */
    const configuredBenchSlots = getRosterSetting(
        rosterSettings,
        "BN"
    );

    playersByPosition.BN
        .slice(configuredBenchSlots)
        .forEach(function (player, index) {
            slots.push({
                id: `BN-extra-${index}`,
                position: "BN",
                player,
                bench: true
            });
        });

    return slots;
}

function canUsePosition(
    player: TeamPlayer,
    targetPosition: string
): boolean {
    if (targetPosition === "QB") {
        return player.positionId === "1";
    }

    if (targetPosition === "RB") {
        return player.positionId === "2";
    }

    if (targetPosition === "WR") {
        return player.positionId === "3";
    }

    if (targetPosition === "FLEX") {
        return (
            player.positionId === "2" ||
            player.positionId === "3"
        );
    }

    if (targetPosition === "K") {
        return player.positionId === "7";
    }

    if (targetPosition === "DEF") {
        return player.positionId === "8";
    }

    return false;
}

function getWeekStatusLabel(status?: string): string {
    const normalizedStatus = String(status || "")
        .trim()
        .toLowerCase();

    if (normalizedStatus === "live") {
        return "En vivo";
    }

    if (normalizedStatus === "scheduled") {
        return "Próximamente";
    }

    if (normalizedStatus === "final") {
        return "Finalizada";
    }

    return "Sin información";
}

function getWeekStatusClass(status?: string): string {
    const normalizedStatus = String(status || "")
        .trim()
        .toLowerCase();

    if (normalizedStatus === "live") {
        return "fantasy-team-status--live";
    }

    if (normalizedStatus === "scheduled") {
        return "fantasy-team-status--scheduled";
    }

    if (normalizedStatus === "final") {
        return "fantasy-team-status--final";
    }

    return "fantasy-team-status--unknown";
}

function getPlayerMatchup(
    player: {
        teamId: string;
    },
    realMatches: RealMatch[],
    scheduleTimezone: string
): string {
    const playerTeamId = String(player.teamId || "").trim();

    if (!playerTeamId) {
        return "BYE";
    }

    const match = realMatches.find(function (currentMatch) {
        const localTeamId = String(
            currentMatch.local?.id || ""
        ).trim();

        const visitorTeamId = String(
            currentMatch.visitor?.id || ""
        ).trim();

        return (
            localTeamId === playerTeamId ||
            visitorTeamId === playerTeamId
        );
    });

    if (!match) {
        return "BYE";
    }

    const localTeamId = String(
        match.local?.id || ""
    ).trim();

    const isLocal = localTeamId === playerTeamId;

    const opponentName = isLocal
        ? String(match.visitor?.name || "").trim()
        : String(match.local?.name || "").trim();

    if (!opponentName) {
        return "BYE";
    }

    const schedule = formatLocalMatchSchedule(
        match.date,
        match.hour,
        scheduleTimezone
    );

    const location = isLocal ? "vs" : "@";

    return [
        schedule,
        location,
        opponentName
    ]
        .filter(Boolean)
        .join(" ");
}

const FantasyTeam: React.FC = () => {
    const router = useIonRouter();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
const activeMainTab = useMemo<
    FantasyMainTab | undefined
>(
    function (): FantasyMainTab | undefined {
        return getFantasyMainTab(
            location.pathname,
            id
        );
    },
    [id, location.pathname]
);

function navigateMainTab(
    selectedTab: FantasyMainTab
): void {
    if (!id || selectedTab === activeMainTab) {
        return;
    }

    router.push(
        getFantasyMainTabPath(
            selectedTab,
            id
        ),
        "none",
        "replace"
    );
}

function goToLeagueHome(): void {
    if (!id) {
        return;
    }

    router.push(
        `/fantasyLeague/${id}`,
        "back",
        "replace"
    );
}

    const [leagueName, setLeagueName] = useState("Liga Fantasy");
    const [teamName, setTeamName] = useState("Mi equipo");
    const [roster, setRoster] = useState<RosterSlot[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectedSlotId, setSelectedSlotId] =
        useState<string | null>(null);

        const [
    selectedFantasyPlayerId,
    setSelectedFantasyPlayerId
] = useState<string | null>(null);
const [
    playerToDrop,
    setPlayerToDrop
] = useState<TeamPlayer | null>(null);

const [
    droppingPlayer,
    setDroppingPlayer
] = useState(false);

const [
    selectedTrade,
    setSelectedTrade
] = useState<FantasyIncomingTrade | null>(null);

const [
    tradeAction,
    setTradeAction
] = useState<"accept" | "cancel" | null>(null);

const [
    respondingTrade,
    setRespondingTrade
] = useState(false);

    const [savingLineup, setSavingLineup] =
        useState(false);
const [state, setState] =
    useState<FantasyTeamState | null>(null);
    const [lineupMessage, setLineupMessage] =
    useState("");
    const pollingRequestRef = useRef(false);
const latestLoadRequestRef = useRef(0);

        /*
    Antes de que la jornada inicie, la alineación puede cambiar.
    Por eso se usa el pronóstico calculado con el roster actual.

    El snapshot sólo debe tener prioridad cuando la jornada ya está
    en vivo o finalizada, porque representa la alineación congelada.
*/
const shouldUseFrozenSnapshot =
    state?.week.status === "live" ||
    state?.week.status === "final";

const activeProjection =
    shouldUseFrozenSnapshot &&
    state?.projectionSnapshot
        ? state.projectionSnapshot
        : state?.projectionForecast ?? null;

const myInitialProjection =
    activeProjection?.myTeam
        ?.totalProjectedPoints ?? null;

const opponentInitialProjection =
    activeProjection?.opponentTeam
        ?.totalProjectedPoints ?? null;

const projectionDate =
    shouldUseFrozenSnapshot
        ? state?.projectionSnapshot?.capturedAt ?? ""
        : state?.projectionForecast?.updatedAt ?? "";

const projectionIsFrozen =
    shouldUseFrozenSnapshot &&
    Boolean(state?.projectionSnapshot);

const loadTeam = useCallback(
    async function (
        forceRefresh = false,
        preserveSelectedSlot = false
    ) {

        if (!isAuthenticated()) {
            router.push("/home", "root", "replace");
            return;
        }
        const requestId =
    latestLoadRequestRef.current + 1;

latestLoadRequestRef.current = requestId;

        try {
            setError("");

            if (!forceRefresh) {
                setLoading(true);
            }

 const result = await fantasyApiGet<
    FantasyTeamState
>(
    "control_FantasyGameState.php" +
        `?id=${encodeURIComponent(id)}`
);

            if (
    requestId !== latestLoadRequestRef.current
) {
    return;
}

setState(result);

            const calculation = calculateFantasyGame(
                result.settings,
                result.fantasyMatch.myTeam.roster || [],
                [],
                result.realMatches || []
            );

            const playerStats = new Map(
                [
                    ...calculation.myTeam.starters,
                    ...calculation.myTeam.bench
                ].map(function (player) {
                    return [
                        player.id,
                        {
                            points: player.points,
                            statLine: player.statLine
                        }
                    ];
                })
            );

            const officialPlayerPoints =
                getOfficialTeamPlayerPoints(
                    result.liveRound,
                    result.fantasyMatch.myTeam.id
                );

            const players: TeamPlayer[] =
                result.fantasyMatch.myTeam.roster.map(
                    function (player) {
const stats = playerStats.get(
    player.id
);

const officialPoints = Number(
    officialPlayerPoints[player.id]
);

return {
    id: player.id,
    name: player.name,
    positionId: player.positionId,
    teamId: player.teamId,
    teamName: player.teamName,
    fantasySlot: player.fantasySlot,
    injury: player.injury || "",
    matchup: getPlayerMatchup(
        player,
        result.realMatches || [],
        result.scheduleTimezone ||
            DEFAULT_SCHEDULE_TIMEZONE
    ),
    /*
        liveRound es la fuente autoritativa del backend.
        Así Mi equipo y Juego muestran exactamente los mismos
        puntos, incluidos fumbles, recuperaciones y reglas DEF.
    */
    points: Number.isFinite(officialPoints)
        ? officialPoints
        : stats?.points ?? 0,

    statLine: stats?.statLine ?? "",

    initialProjection:
    (
        result.week.status === "live" ||
        result.week.status === "final"
    )
        ? (
            result.projectionSnapshot?.myTeam?.players[
                player.id
            ] ??
            getPlayerProjection(
                result.projectionForecast?.myTeam?.players[
                    player.id
                ]
            )
        )
        : getPlayerProjection(
            result.projectionForecast?.myTeam?.players[
                player.id
            ]
        ) ??
        result.projectionSnapshot?.myTeam?.players[
            player.id
        ] ??
        null,

    isLocked: Boolean(player.isLocked)
};
                    }
                );

            setLeagueName(
                result.league.name || "Liga Fantasy"
            );

            setTeamName(
                result.fantasyMatch.myTeam.name ||
                    "Mi equipo"
            );

            setRoster(
                createRosterSlots(
                    result.rosterSettings || {},
                    players
                )
            );

            if (!preserveSelectedSlot) {
    setSelectedSlotId(null);
}

        } catch (requestError) {
    if (
        requestId !== latestLoadRequestRef.current
    ) {
        return;
    }

    console.error(
        "No fue posible cargar el equipo Fantasy:",
        requestError
    );

    setError(
        requestError instanceof Error
            ? requestError.message
            : "No fue posible cargar la información de tu equipo."
    );
} finally {
    if (
        requestId === latestLoadRequestRef.current
    ) {
        setLoading(false);
    }
}

    },
    [id, router]
);

function formatFantasyPoints(
    points: number | null | undefined
): string {
    if (
        typeof points !== "number" ||
        Number.isNaN(points)
    ) {
        return "—";
    }

    return points.toFixed(1);
}

function getPlayerProjection(
    value: LiveProjectionPlayer | number | undefined | null
): number | null {
    if (typeof value === "number") {
        return Number.isFinite(value)
            ? value
            : null;
    }

    if (
        typeof value === "object" &&
        value !== null
    ) {
        /*
            Antes de iniciar el partido projection y
            pregameProjection tienen el mismo valor.

            Se prioriza pregameProjection para conservar la
            proyección original cuando el partido ya está en vivo.
        */
        const projection = Number(
            value.pregameProjection ??
            value.projection
        );

        return Number.isFinite(projection)
            ? projection
            : null;
    }

    return null;
}

const changeLineup = useCallback(
    async function (
        starterPlayerId: string,
        benchPlayerId: string,
        targetPosition: string
    ): Promise<void> {
        try {
            setLineupMessage("");
            setSavingLineup(true);

const result = await fantasyApiPost<{
    success: boolean;
    message?: string;
}>(
    "control_Team.php",
    {
        leagueId: id,
        starterPlayerId,
        benchPlayerId,
        targetPosition
    }
);

            /*
                Recarga completa: actualiza slots, puntos, bloqueos
                y cierra cualquier selector abierto.
            */
            await loadTeam(true);
        } catch (requestError) {

            if (
                requestError instanceof FantasyApiError &&
                (
                    requestError.status === 409 ||
                    requestError.status === 423
                )
            ) {
                await loadTeam(true, true);
            }

            console.error(
                "No fue posible actualizar la alineación Fantasy:",
                requestError
            );

            setLineupMessage(
                requestError instanceof Error
                    ? requestError.message
                    : "No fue posible actualizar la alineación."
            );
        } finally {
            setSavingLineup(false);
        }
    },
    [id, loadTeam]
);
    useEffect(
        function () {
            loadTeam();
        },
        [loadTeam]
    );

    /*
    Recarga silenciosa para detectar:
    - inicio de partidos,
    - jugadores bloqueados,
    - cambios de roster desde otro dispositivo,
    - puntos y estadísticas nuevas.
*/
/*
    Actualización automática de acuerdo con el estado real
    de la jornada.

    live       → 30 segundos
    scheduled  → 60 segundos
    final      → no hace polling
*/
useEffect(
    function () {
        const weekStatus =
            state?.week.status || "scheduled";

        if (weekStatus === "final") {
            return;
        }

        const intervalMilliseconds =
            weekStatus === "live"
                ? 30 * 1000
                : 60 * 1000;

        const intervalId = window.setInterval(
            function () {
                if (
                    document.visibilityState !== "visible" ||
savingLineup ||
respondingTrade ||
pollingRequestRef.current

                ) {
                    return;
                }

                pollingRequestRef.current = true;

                loadTeam(true, true).finally(function () {
                    pollingRequestRef.current = false;
                });
            },
            intervalMilliseconds
        );

        return function () {
            window.clearInterval(intervalId);
        };
    },
[
    loadTeam,
    savingLineup,
    respondingTrade,
    state?.week.status
]

);

/*
    Cuando el usuario vuelve a la pestaña o a la aplicación,
    consulta el roster inmediatamente en lugar de esperar
    al siguiente intervalo de un minuto.
*/
useEffect(
    function () {
        function handleVisibilityChange() {
            if (
                document.visibilityState !== "visible" ||
savingLineup ||
respondingTrade ||
pollingRequestRef.current

            ) {
                return;
            }

            pollingRequestRef.current = true;

            loadTeam(true, true).finally(function () {
                pollingRequestRef.current = false;
            });
        }

        document.addEventListener(
            "visibilitychange",
            handleVisibilityChange
        );

        return function () {
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        };
    },
    [loadTeam, savingLineup, respondingTrade]

);


    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadTeam(true);
        event.detail.complete();
    }

    const starters = roster.filter(function (slot) {
        return !slot.bench;
    });

    const bench = roster.filter(function (slot) {
        return slot.bench;
    });

    const selectedSlot = roster.find(function (slot) {
    return slot.id === selectedSlotId;
});

const availableBenchPlayers = selectedSlot
    ? bench.filter(function (slot) {
        if (!slot.player) {
            return false;
        }

        if (slot.player.isLocked) {
            return false;
        }

        return canUsePosition(
            slot.player,
            selectedSlot.position
        );
    })
    : [];


const occupiedSlots = roster.filter(function (slot) {
    return Boolean(slot.player);
}).length;

const weeklyPoints = starters.reduce(
    function (total, slot) {
        return total + (slot.player?.points ?? 0);
    },
    0
);
const weekStatus = state?.week.status || "";

const isByeWeek = Boolean(
    state?.fantasyMatch.isBye
);

const weekLabel = isByeWeek
    ? "Semana de descanso"
    : getWeekStatusLabel(weekStatus);

const weekStatusClass = isByeWeek
    ? "fantasy-team-status--bye"
    : getWeekStatusClass(weekStatus);
function toggleLineupSelector(slot: RosterSlot): void {
    if (
        savingLineup ||
        slot.player?.isLocked
    ) {
        return;
    }

    setSelectedSlotId(function (currentSlotId) {
        return currentSlotId === slot.id
            ? null
            : slot.id;
    });
}
async function handleDropFromPlayerModal(
    playerId: string
): Promise<void> {
    const player = roster
        .map(function (slot) {
            return slot.player;
        })
        .find(function (
            currentPlayer
        ): currentPlayer is TeamPlayer {
            return currentPlayer?.id === playerId;
        });

    if (!player) {
        setLineupMessage(
            "El jugador ya no forma parte de tu roster."
        );

        setSelectedFantasyPlayerId(null);

        await loadTeam(true);

        return;
    }

    if (player.isLocked) {
        setLineupMessage(
            "No puedes soltar este jugador porque su partido ya inició."
        );

        return;
    }

    /*
        Se cierra la ficha antes de abrir la confirmación.
    */
    setSelectedFantasyPlayerId(null);
    setPlayerToDrop(player);
}
async function confirmDropFromTeam(): Promise<void> {
    if (!playerToDrop || droppingPlayer) {
        return;
    }

    try {
        setDroppingPlayer(true);
        setLineupMessage("");

const result = await fantasyApiPost<{
    success: boolean;
    message?: string;
}>(
    "control_DropFantasyPlayer.php",
    {
        leagueId: id,
        playerId: playerToDrop.id
    }
);
        setLineupMessage(
            result.message ||
                `${playerToDrop.name} fue liberado correctamente.`
        );

        setPlayerToDrop(null);

        /*
            Refresca roster, puntos, bloqueos y alineación.
        */
        await loadTeam(true);
    } catch (requestError) {
        console.error(
            "No fue posible soltar jugador Fantasy:",
            requestError
        );

        setLineupMessage(
            requestError instanceof Error
                ? requestError.message
                : "No fue posible soltar al jugador."
        );
    } finally {
        setDroppingPlayer(false);
    }
}

async function respondToTrade(
    action: "accept" | "cancel"
): Promise<void> {
    if (
        !selectedTrade ||
        respondingTrade
    ) {
        return;
    }

    try {
        setRespondingTrade(true);
        setLineupMessage("");

 const result = await fantasyApiPost<{
    success: boolean;
    message?: string;
}>(
    "control_RespondFantasyTrade.php",
    {
        leagueId: id,
        tradeId: selectedTrade.id,
        action
    }
);
        setLineupMessage(
            result.message ||
                (
                    action === "accept"
                        ? "Intercambio aceptado correctamente."
                        : "Solicitud de intercambio cancelada."
                )
        );

        setSelectedTrade(null);
        setTradeAction(null);

        /*
            Es indispensable recargar, porque aceptar un trade
            modifica ambos rosters, slots, puntos y bloqueos.
        */
        await loadTeam(true);
    } catch (requestError) {
        console.error(
            "No fue posible responder el intercambio:",
            requestError
        );

        setLineupMessage(
            requestError instanceof Error
                ? requestError.message
                : "No fue posible actualizar el intercambio."
        );
    } finally {
        setRespondingTrade(false);
    }
}
const receivedTrades =
    state?.trades?.received || [];

const sentTrades =
    state?.trades?.sent || [];

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="fantasy-team-toolbar">
    <IonButtons slot="start">
        <IonButton
            fill="clear"
            aria-label="Regresar a la liga"
            onClick={goToLeagueHome}
        >
            <IonIcon icon={arrowBackOutline} />
        </IonButton>
    </IonButtons>

    <IonTitle>Mi equipo</IonTitle>

    <FantasyHeaderActions
    leagueId={id || ""}
    screen="team"
    />
</IonToolbar>
<IonToolbar className="fantasy-navigation-toolbar">
    <IonSegment
        value={activeMainTab || ""}
        onIonChange={function (event) {
            const selectedTab = String(
                event.detail.value || ""
            ) as FantasyMainTab;

            if (
                ![
                    "home",
                    "players",
                    "team",
                    "game"
                ].includes(selectedTab)
            ) {
                return;
            }

            navigateMainTab(selectedTab);
        }}
    >
        <IonSegmentButton value="home">
            Inicio
        </IonSegmentButton>

        <IonSegmentButton value="players">
            Jugadores
        </IonSegmentButton>

        <IonSegmentButton value="team">
            Mi equipo
        </IonSegmentButton>

        <IonSegmentButton value="game">
            Juego
        </IonSegmentButton>
    </IonSegment>
</IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="fantasy-team-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando equipo..."
                    />
                </IonRefresher>

                <section className="fantasy-team-hero">
                    <div className="fantasy-team-hero__icon">
                        <IonIcon icon={shirtOutline} />
                    </div>

                    <div>
                        <span>{leagueName}</span>
                        <h1>{teamName}</h1>
                        <p>Gestiona tu roster para competir esta temporada.</p>
                    </div>
                </section>

                {loading && (
                    <IonProgressBar
                        className="fantasy-team-progress"
                        type="indeterminate"
                    />
                )}

                {!loading && error && (
                    <main className="fantasy-team-main">
                        <div className="fantasy-team-empty">
                            <IonIcon icon={refreshOutline} />
                            <strong>No pudimos cargar tu equipo</strong>
                            <span>{error}</span>

                            <IonButton
                                fill="outline"
                                size="small"
                                onClick={function () {
                                    loadTeam(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    </main>
                )}

                {!loading && !error && (
                    <main className="fantasy-team-main">
                        {lineupMessage && (
    <div
        className="fantasy-team-lineup-message"
        role="alert"
    >
        <span>{lineupMessage}</span>

        <IonButton
            size="small"
            fill="clear"
            color="medium"
            onClick={function () {
                setLineupMessage("");
            }}
        >
            Cerrar
        </IonButton>
    </div>
)}
{receivedTrades.length > 0 && (
    <section className="fantasy-team-section">
        <div className="fantasy-team-section__heading">
            <div>
                <span>INTERCAMBIOS</span>

                <h2>
                    Solicitudes recibidas
                </h2>
            </div>

            <IonIcon icon={personOutline} />
        </div>

        <div className="fantasy-team-trades">
            {receivedTrades.map(function (trade) {
                const hasLockedPlayer =
                    trade.offeredPlayer.isLocked ||
                    trade.requestedPlayer.isLocked;

                return (
                    <article
                        key={trade.id}
                        className="fantasy-team-trade"
                    >
                        <div className="fantasy-team-trade__manager">
                            <span>PROPUESTA DE</span>

                            <strong>
{trade.counterpartyTeamName ||
    trade.counterpartyManagerName ||
    "Otro equipo"}

                            </strong>
                        </div>

                        <div className="fantasy-team-trade__players">
                            <div>
                                <span>RECIBES</span>

                                <strong>
                                    {trade.offeredPlayer.name}
                                </strong>

                                <small>
                                    {trade.offeredPlayer.position}
                                    {" · "}
                                    {trade.offeredPlayer.teamName}
                                </small>
                            </div>

                            <div className="fantasy-team-trade__arrow">
                                →
                            </div>

                            <div>
                                <span>ENTREGAS</span>

                                <strong>
                                    {trade.requestedPlayer.name}
                                </strong>

                                <small>
                                    {trade.requestedPlayer.position}
                                    {" · "}
                                    {trade.requestedPlayer.teamName}
                                </small>
                            </div>
                        </div>

                        {hasLockedPlayer && (
                            <p className="fantasy-team-trade__warning">
                                Esta propuesta contiene un jugador
                                bloqueado y no puede aceptarse.
                            </p>
                        )}

                        <div className="fantasy-team-trade__actions">
                            <IonButton
                                size="small"
                                fill="outline"
                                color="medium"
                                disabled={respondingTrade}
                                onClick={function () {
                                    setSelectedTrade(trade);
                                    setTradeAction("cancel");
                                }}
                            >
                                Cancelar
                            </IonButton>

                            <IonButton
                                size="small"
                                disabled={
                                    respondingTrade ||
                                    hasLockedPlayer
                                }
                                onClick={function () {
                                    setSelectedTrade(trade);
                                    setTradeAction("accept");
                                }}
                            >
                                Aceptar
                            </IonButton>
                        </div>
                    </article>
                );
            })}
        </div>
    </section>
)}
{sentTrades.length > 0 && (
    <section className="fantasy-team-section">
        <div className="fantasy-team-section__heading">
            <div>
                <span>INTERCAMBIOS</span>

                <h2>
                    Solicitudes enviadas
                </h2>
            </div>
        </div>

        <div className="fantasy-team-trades">
            {sentTrades.map(function (trade) {
                return (
                    <article
                        key={trade.id}
                        className="fantasy-team-trade fantasy-team-trade--sent"
                    >
                        <div className="fantasy-team-trade__manager">
                            <span>ENVIADA A</span>

                            <strong>
{trade.counterpartyTeamName ||
    trade.counterpartyManagerName ||
    "Otro equipo"}

                            </strong>
                        </div>

                        <div className="fantasy-team-trade__players">
                            <div>
                                <span>OFRECES</span>

                                <strong>
                                    {trade.offeredPlayer.name}
                                </strong>
                            </div>

                            <div className="fantasy-team-trade__arrow">
                                →
                            </div>

                            <div>
                                <span>SOLICITAS</span>

                                <strong>
                                    {trade.requestedPlayer.name}
                                </strong>
                            </div>
                        </div>

                        <IonButton
                            size="small"
                            fill="outline"
                            color="danger"
                            disabled={respondingTrade}
                            onClick={function () {
                                setSelectedTrade(trade);
                                setTradeAction("cancel");
                            }}
                        >
                            Cancelar propuesta
                        </IonButton>
                    </article>
                );
            })}
        </div>
    </section>
)}

{isByeWeek && (
    <section className="fantasy-team-bye">
        <IonIcon icon={trophyOutline} />

        <div>
            <strong>Semana de descanso</strong>

            <span>
                Tu equipo no tiene rival asignado en esta
                jornada. Puedes revisar y preparar tu roster
                para la siguiente semana.
            </span>
        </div>
    </section>
)}

    {(myInitialProjection !== null ||
        opponentInitialProjection !== null) && (
        <section className="fantasy-projection-score">
            <div className="fantasy-projection-score__team">
                <span>MI EQUIPO</span>

                <strong>
                    {formatFantasyPoints(
                        myInitialProjection
                    )}
                </strong>

                <small>
                    {projectionIsFrozen
                        ? "PROY. INICIAL"
                        : "PRONÓSTICO"}
                </small>
            </div>

            <div className="fantasy-projection-score__divider">
                VS
            </div>

            <div className="fantasy-projection-score__team">
                <span>
    {isByeWeek ? "SIN RIVAL" : "RIVAL"}
</span>


                <strong>
                    {isByeWeek
    ? "—"
    : formatFantasyPoints(
        opponentInitialProjection
    )}

                </strong>

                <small>
                    {projectionIsFrozen
                        ? "PROY. INICIAL"
                        : "PRONÓSTICO"}
                </small>
            </div>

            {projectionDate && (
                <p className="fantasy-projection-score__date">
                    {projectionIsFrozen
                        ? "Proyección congelada: "
                        : "Pronóstico actualizado: "}

                    {new Date(
                        projectionDate
                    ).toLocaleString("es-MX", {
                        dateStyle: "medium",
                        timeStyle: "short"
                    })}
                </p>
            )}
        </section>
    )}

    <section className="fantasy-team-summary">
        <div>

                                <span>ROSTER</span>
                                <strong>
                                    {occupiedSlots} / {roster.length}
                                </strong>
                            </div>

                            <div>
                                <span>PUNTOS SEMANA</span>
                                <strong>{formatPoints(weeklyPoints)}</strong>
                            </div>


                            <div>
    <span>ESTADO</span>

    <strong
        className={[
            "fantasy-team-status",
            savingLineup
                ? "fantasy-team-status--saving"
                : weekStatusClass
        ].join(" ")}
    >
        {savingLineup
            ? "Guardando..."
            : weekLabel}
    </strong>
</div>

                        </section>

                        <section className="fantasy-team-section">
                            <div className="fantasy-team-section__heading">
                                <div>
                                    <span>ALINEACIÓN</span>
                                    <h2>Titulares</h2>
                                </div>

                                <IonIcon icon={americanFootballOutline} />
                            </div>

                            <div className="fantasy-team-roster">
                                {starters.map(function (slot) {
    const isSelected = selectedSlotId === slot.id;

    const injury = getFantasyInjuryInfo(
        slot.player?.injury
    );

    return (
        <div key={slot.id}>
            <article
    className={[
        "fantasy-team-player",
        "fantasy-team-player--selectable",
        slot.player?.isLocked
            ? "fantasy-team-player--locked"
            : ""
    ].join(" ")}
    onClick={function () {
        /*
            Un jugador existente abre su perfil.
            Un slot vacío permite seleccionar un suplente.
        */
        if (slot.player) {
            setSelectedSlotId(null);
            setSelectedFantasyPlayerId(slot.player.id);

            return;
        }

        toggleLineupSelector(slot);
    }}
>

                <button
    type="button"
    className={[
        "fantasy-team-position",
        "fantasy-team-position--button",
        `fantasy-team-position--${
            slot.position === "FLEX"
                ? "wb"
                : slot.position.toLowerCase()
        }`
    ].join(" ")}
    disabled={
        savingLineup ||
        Boolean(slot.player?.isLocked)
    }
    aria-label={
        slot.player
            ? `Cambiar jugador de la posición ${slot.position}`
            : `Elegir jugador para la posición ${slot.position}`
    }
    onClick={function (event) {
        event.stopPropagation();

        toggleLineupSelector(slot);
    }}
>
    {slot.position}
</button>


                <div className="fantasy-team-player__details">
<div className="fantasy-team-player__name-row">
    <strong>
        {slot.player?.name || "Vacante"}
    </strong>

    {injury.status && (
        <span
            className={[
                "fantasy-injury-badge",
                `fantasy-injury-badge--${injury.status}`
            ].join(" ")}
            title={injury.description}
            aria-label={injury.description}
        >
            <IonIcon icon={medkitOutline} />

            {injury.label}
        </span>
    )}
</div>

    {slot.player ? (
        <>
            <small className="fantasy-team-player__team">
                {slot.player.teamName || "Sin equipo"}
            </small>
            <small className="fantasy-team-player__matchup">
                {slot.player.matchup}
            </small>
            <small className="fantasy-team-player__stats">
                {slot.player.statLine || "Sin estadísticas"}
            </small>
        </>
    ) : (
        <small className="fantasy-team-player__empty-slot">
            Toca para elegir un jugador de banca
        </small>
    )}

    {slot.player?.isLocked && (
        <small className="fantasy-team-player__locked">
            PARTIDO INICIADO
        </small>
    )}
</div>

                {slot.player ? (
                    <div className="fantasy-team-player__points">
    <strong>
        {formatPoints(slot.player.points)}
    </strong>

    <small>PTS</small>

    <em>
        PROY.{" "}
        {formatFantasyPoints(
            slot.player.initialProjection
        )}
    </em>
</div>

                ) : null}

                <IonIcon
                    icon={
                        slot.player
                            ? personOutline
                            : trophyOutline
                    }
                />
            </article>

            {isSelected && (
                <div className="fantasy-team-lineup-options">
                    <strong>
                        Elegir jugador para {slot.position}
                    </strong>

                    {availableBenchPlayers.length === 0 ? (
                        <span>
                            No hay jugadores de banca elegibles.
                        </span>
                    ) : (
                        availableBenchPlayers.map(
                            function (benchSlot) {
                                const benchPlayer =
                                    benchSlot.player;

                                if (!benchPlayer) {
                                    return null;
                                }

                                return (
                                    <IonButton
                                        key={benchPlayer.id}
                                        size="small"
                                        fill="outline"
                                        disabled={
    savingLineup ||
    Boolean(benchPlayer.isLocked)
}

                                        onClick={async function (event) {
                                            event.stopPropagation();

                                            await changeLineup(
                                                slot.player?.id || "",
                                                benchPlayer.id,
                                                slot.position
                                            );
                                        }}
                                    >
                                        {benchPlayer.name}
                                    </IonButton>
                                );
                            }
                        )
                    )}

                    <IonButton
                        size="small"
                        fill="clear"
                        color="medium"
                        disabled={savingLineup}
                        onClick={function (event) {
                            event.stopPropagation();
                            setSelectedSlotId(null);
                        }}
                    >
                        Cancelar
                    </IonButton>
                </div>
            )}
        </div>
    );
})}

                            </div>
                        </section>

                        {bench.length > 0 && (
                            <section className="fantasy-team-section">
                                <div className="fantasy-team-section__heading">
                                    <div>
                                        <span>SUPLENTES</span>
                                        <h2>Banca</h2>
                                    </div>

                                    <IonIcon icon={personOutline} />
                                </div>

                                <div className="fantasy-team-roster">
                                    {bench.map(function (slot) {
    const injury = getFantasyInjuryInfo(
        slot.player?.injury
    );
    return (
        <article
    className={[
        "fantasy-team-player",
        "fantasy-team-player--bench",
        "fantasy-team-player--selectable",
        slot.player?.isLocked
            ? "fantasy-team-player--locked"
            : ""
    ].join(" ")}
    key={slot.id}
onClick={function () {
    if (!slot.player) {
        return;
    }

    setSelectedSlotId(null);
    setSelectedFantasyPlayerId(slot.player.id);
}}

>

            <span className="fantasy-team-position fantasy-team-position--bn">
                BN
            </span>

<div className="fantasy-team-player__details">
<div className="fantasy-team-player__name-row">
    <strong title={slot.player?.name || "Vacante"}>
        {slot.player?.name || "Vacante"}
    </strong>

    {slot.player ? (
        <span
            className={[
                "fantasy-team-player__bench-position",
                `fantasy-team-player__bench-position--${getPlayerPositionLabel(
                    slot.player.positionId
                ).toLowerCase()}`
            ].join(" ")}
            aria-label={`Posición ${getPlayerPositionLabel(
                slot.player.positionId
            )}`}
        >
            <span aria-hidden="true">·</span>

            {getPlayerPositionLabel(slot.player.positionId)}
        </span>
    ) : null}

    {injury.status && (
        <span
            className={[
                "fantasy-injury-badge",
                `fantasy-injury-badge--${injury.status}`
            ].join(" ")}
            title={injury.description}
            aria-label={injury.description}
        >
            <IonIcon icon={medkitOutline} />

            {injury.label}
        </span>
    )}
</div>

    {slot.player ? (
        <>
            <small className="fantasy-team-player__team">
                {slot.player.teamName || "Sin equipo"}
            </small>
            <small className="fantasy-team-player__matchup">
                {slot.player.matchup}
            </small>
            <small className="fantasy-team-player__stats">
                {slot.player.statLine || "Sin estadísticas"}
            </small>
        </>
    ) : (
        <small className="fantasy-team-player__empty-slot">
            Espacio disponible en la banca
        </small>
    )}

    {slot.player?.isLocked && (
        <small className="fantasy-team-player__locked">
            PARTIDO INICIADO
        </small>
    )}
</div>


            {slot.player ? (
                <div className="fantasy-team-player__points">
    <strong>
        {formatPoints(slot.player.points)}
    </strong>

    <small>PTS</small>

    <em>
        PROY.{" "}
        {formatFantasyPoints(
            slot.player.initialProjection
        )}
    </em>
</div>

            ) : null}

            <IonIcon
                icon={
                    slot.player
                        ? personOutline
                        : trophyOutline
                }
            />
        </article>
    );
})}

                                </div>
                            </section>
                        )}

                        {roster.length === 0 && (
                            <div className="fantasy-team-empty">
                                <IonIcon icon={americanFootballOutline} />
                                <strong>No hay slots configurados</strong>
                                <span>
                                    No se encontró la configuración de roster
                                    para esta liga.
                                </span>
                            </div>
                        )}
                    </main>
                )}
            </IonContent>
<FantasyPlayerModal
    leagueId={id || ""}
    playerId={selectedFantasyPlayerId || ""}
    isOpen={selectedFantasyPlayerId !== null}
    onDismiss={function () {
        setSelectedFantasyPlayerId(null);
    }}
    onDropPlayer={handleDropFromPlayerModal}
/>
<IonAlert
    isOpen={playerToDrop !== null}
    header="¿Soltar jugador?"
    message={
        playerToDrop
            ? `Liberarás a <strong>${playerToDrop.name}</strong>. ` +
                "El jugador volverá a estar disponible para todos los equipos."
            : ""
    }
    backdropDismiss={!droppingPlayer}
    onDidDismiss={function () {
        if (!droppingPlayer) {
            setPlayerToDrop(null);
        }
    }}
    buttons={[
        {
            text: "Cancelar",
            role: "cancel",
            handler: function () {
                if (!droppingPlayer) {
                    setPlayerToDrop(null);
                }
            }
        },
        {
            text: droppingPlayer
                ? "Liberando..."
                : "Soltar jugador",
            role: "destructive",
            handler: function () {
                void confirmDropFromTeam();

                /*
                    Mantiene el alert abierto durante la petición.
                */
                return false;
            }
        }
    ]}
/>
<IonAlert
    isOpen={
        selectedTrade !== null &&
        tradeAction !== null
    }
    header={
        tradeAction === "accept"
            ? "¿Aceptar intercambio?"
            : "¿Cancelar intercambio?"
    }
    message={
        selectedTrade
            ? tradeAction === "accept"
                ? `Recibirás a <strong>${selectedTrade.offeredPlayer.name}</strong> ` +
                    `y entregarás a <strong>${selectedTrade.requestedPlayer.name}</strong>.`
                : "La solicitud dejará de estar disponible."
            : ""
    }
    backdropDismiss={!respondingTrade}
    onDidDismiss={function () {
        if (!respondingTrade) {
            setSelectedTrade(null);
            setTradeAction(null);
        }
    }}
    buttons={[
        {
            text: "Volver",
            role: "cancel",
            handler: function () {
                if (!respondingTrade) {
                    setSelectedTrade(null);
                    setTradeAction(null);
                }
            }
        },
        {
            text:
                tradeAction === "accept"
                    ? "Aceptar"
                    : "Cancelar solicitud",
            role:
                tradeAction === "cancel"
                    ? "destructive"
                    : undefined,
            handler: function () {
                if (tradeAction) {
                    void respondToTrade(tradeAction);
                }

                return false;
            }
        }
    ]}
/>


        </IonPage>
    );
};

export default FantasyTeam;
